import {
  createFocusOutlineStyle,
  makeResetStyles,
  makeStyles,
  mergeClasses,
  slot,
  typographyStyles,
} from '@fluentui/react-components';
import { tokens } from '@fluentui-copilot/tokens';

import type { CopilotMessageSlots, CopilotMessageState } from './CopilotMessage.types';
import type { SlotClassNames } from '@fluentui/react-components';

export const copilotMessageClassNames: SlotClassNames<CopilotMessageSlots> = {
  root: 'fai-CopilotMessage',
  accessibleHeading: 'fai-CopilotMessage__accessibleHeading',
  avatar: 'fai-CopilotMessage__avatar',
  name: 'fai-CopilotMessage__name',
  disclaimer: 'fai-CopilotMessage__disclaimer',
  content: 'fai-CopilotMessage__content',
  footnote: 'fai-CopilotMessage__footnote',
  actions: 'fai-CopilotMessage__actions',
  progress: 'fai-CopilotMessage__progress',
};

const indeterminateProgressBarReducedMotion = {
  '0%': {
    opacity: '.2',
  },
  '50%': {
    opacity: '1',
  },
  '100%': {
    opacity: '.2',
  },
};

const useRootClassName = makeResetStyles({
  backgroundColor: 'transparent',
  borderRadius: tokens.borderRadiusXLarge,
  columnGap: tokens.spacingHorizontalS,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  rowGap: tokens.spacingHorizontalS,
  flexWrap: 'wrap',

  ...createFocusOutlineStyle({
    style: {
      outlineRadius: tokens.borderRadiusXLarge,
    },
  }),
});

/**
 * Styles for the heading wrapper slot
 */
const useHeadingWrapperClassName = makeResetStyles({
  display: 'flex',
  columnGap: tokens.spacingHorizontalS,
  rowGap: tokens.spacingHorizontalS,
  flexWrap: 'wrap',
});

/**
 * Styles for the hidden heading slot
 */
const useHeadingClassName = makeResetStyles({
  clip: 'rect(0px, 0px, 0px, 0px)',
  height: '1px',
  margin: '-1px',
  width: '1px',
  position: 'absolute',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

/**
 * Styles for the avatar slot
 */
const useAvatarClassName = makeResetStyles({
  alignItems: 'center',
  display: 'flex',
});

/**
 * Styles for the name slot
 */
const useNameClassName = makeResetStyles({
  alignContent: 'center',
  ...typographyStyles.body1Strong,
});

/**
 * Styles for the disclaimer slot
 */
const useDisclaimerClassName = makeResetStyles({
  alignContent: 'center',
});

/**
 * Styles for the content slot
 */
const useContentClassName = makeResetStyles({
  display: 'flex',
  flexDirection: 'column',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
});

/**
 * Styles for the footer slot
 */
const useFootnoteClassName = makeResetStyles({
  alignItems: 'start',
  columnGap: tokens.spacingHorizontalXS,
  display: 'flex',
});

/**
 * Styles for the actions slot
 */
const useActionsClassName = makeResetStyles({
  columnGap: tokens.spacingHorizontalXXS,
  display: 'flex',
});

const useStyles = makeStyles({
  canvas: {
    marginLeft: tokens.spacingHorizontalXXXL,
  },
  bar: {
    animationDuration: '3s',
    backgroundColor: tokens.colorTransparentBackground,
    backgroundImage: `linear-gradient(90deg, ${tokens.colorTransparentStroke} 0%, ${tokens.colorBrandFlair1} 35%, ${tokens.colorBrandFlair2} 70%, ${tokens.colorBrandFlair3} 92%,  ${tokens.colorTransparentStroke})`,
    '@media screen and (prefers-reduced-motion: reduce)': {
      maxWidth: '100%',
      animationIterationCount: 'infinite',
      animationName: indeterminateProgressBarReducedMotion,
      animationDuration: '3s',
    },
  },
  indeterminateBarNotIsLoading: {
    animationPlayState: 'paused',
  },
});

export const useCopilotMessageStyles_unstable = (state: CopilotMessageState): CopilotMessageState => {
  'use no memo';

  const rootClassName = useRootClassName();
  const headingWrapperClassName = useHeadingWrapperClassName();
  const headingClassName = useHeadingClassName();
  const avatarClassName = useAvatarClassName();
  const nameClassName = useNameClassName();
  const disclaimerClassName = useDisclaimerClassName();
  const contentClassName = useContentClassName();
  const footnoteClassName = useFootnoteClassName();
  const actionClassName = useActionsClassName();
  const styles = useStyles();

  state.root.className = mergeClasses(copilotMessageClassNames.root, rootClassName, state.root.className);
  state.nameLineClassName = headingWrapperClassName;

  if (state.accessibleHeading) {
    state.accessibleHeading.className = mergeClasses(
      copilotMessageClassNames.accessibleHeading,
      headingClassName,
      state.accessibleHeading.className,
    );
  }
  state.avatar.className = mergeClasses(copilotMessageClassNames.avatar, avatarClassName, state.avatar.className);

  state.name.className = mergeClasses(copilotMessageClassNames.name, nameClassName, state.name.className);

  if (state.disclaimer) {
    state.disclaimer.className = mergeClasses(
      copilotMessageClassNames.disclaimer,
      disclaimerClassName,
      state.disclaimer.className,
    );
  }

  if (state.content) {
    state.content.className = mergeClasses(
      copilotMessageClassNames.content,
      contentClassName,
      state.mode === 'canvas' && styles.canvas,
      state.content.className,
    );
  }

  if (state.footnote) {
    state.footnote.className = mergeClasses(
      copilotMessageClassNames.footnote,
      footnoteClassName,
      state.mode === 'canvas' && styles.canvas,
      state.footnote.className,
    );
  }

  if (state.actions) {
    state.actions.className = mergeClasses(
      copilotMessageClassNames.actions,
      actionClassName,
      state.mode === 'canvas' && styles.canvas,
      state.actions.className,
    );
  }

  if (state.progress) {
    state.progress.className = mergeClasses(copilotMessageClassNames.progress, state.progress.className);

    if (state.progress.value === undefined) {
      const bar = slot.always(state.progress.bar, { elementType: 'div' });
      bar.className = mergeClasses(
        styles.bar,
        state.loadingState !== 'loading' && styles.indeterminateBarNotIsLoading,
        bar.className,
      );
      state.progress.bar = bar;
    }
  }

  return state;
};
